import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import { raiseToast } from '../../../services/redux/reducers/toast/toast';
import { TextInputField, Button, DropDownField, InputSwitchField, AutoCompleteField } from "../../../helpers";
import { insertPettyCashAccount, updatePettyCashAccount } from '../../../services/api/pettyCashAccounts';
import { PettyCashAccountsSchem } from '../../../validation/addPettyCashAccountValidation';
import { validateInputs } from '../../../utils';
import { updatePettyCashAccountsData } from "../../../services/redux/reducers/pettyCashAccounts/pettyCashAccounts";

const AddPettyCashAccountModal = (props) => {
    const {initialValues, usersList, title, setVisibleAddPettyCashAccount, setPettyCashAccountLoading } = props;
    const [accountName, setAccountName] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [removedUsers, setRemovedUsers] = useState([]);
    const [initialAccountUsers,setInitialAccountUsers] = useState([])
    const [primaryUser, setPrimaryUser] = useState('');
    const [accountType, setAccountType] = useState('');
    const [status, setStatus] = useState('');
    const [formErrors, setFormErrors] = useState([])
    const [selectedPettyCashAccountsId, setSelectedPettyCashAccountsId] = useState(null)
    const userDetails = useSelector(state => state.userDetails);
    const userId = userDetails?.userId
    const pettyCashAccountsData = useSelector(state => state.pettyCashAccounts.pettyCashAccountsData);

    const dispatch = useDispatch()

    useEffect(() => {
        if (initialValues) {
            setSelectedPettyCashAccountsId(initialValues?.pettyCashAccountsId)
            setPrimaryUser(initialValues?.userId)
            setAccountName(initialValues.pettyCashAccount || '');
            const initialSelectedUsers = initialValues.accountUsers?.map(user => {
                const userObj = usersList.find(u => u.userId === user.userId);
                if (userObj) {
                    return {
                        label: `${userObj?.firstName} ${userObj?.lastName}`,
                        value: user.userId 
                    };
                } else {
                    return null;
                }
            }).filter(user => user !== null);
            const initialAccountUserList = initialSelectedUsers?.map(user => user.value);
            setSelectedUsers(initialSelectedUsers);
            setInitialAccountUsers(initialAccountUserList);
            setAccountType(initialValues.accountType || '');
            setStatus(initialValues.status || '');
        }
    }, []);
    useEffect(() => {
        const removed = [];
        initialAccountUsers.forEach(user => {
            if (!selectedUsers.some(selectedUser => selectedUser.value === user)) {
                removed.push(user)
            }
        });
        setRemovedUsers(removed);

    if (primaryUser && !selectedUsers.some(user => user.value === primaryUser)) {
        setPrimaryUser(null);
    }
    }, [selectedUsers]);

    const getUsersNamesFromIds = (usersList, errorArray) => {
        const userNames = [];
        usersList.forEach(user => {
            if (errorArray.includes(user.userId)) {
                userNames.push(`${user.firstName} ${user.lastName}`);
            }
        });
        return userNames;
    };
    const handleAddAccount = async () =>{
        let selectedUserDetails = selectedUsers?.map(user => ({
            userId : user.value,
            statusId : 1
        }));
        let selectedUserIds = selectedUsers?.map(user => user.value)
        const data = {
            pettyCashAccount : accountName,
            userId : primaryUser,
            pettyCashUsers: selectedUserDetails,
            pettyCashUsersIds : selectedUserIds,
            createdBy : userId,
            updatedBy: userId,
            createdAt: new Date(),
            updatedAt: new Date(),
            accountType : 'Joint',
        }
        const validationRes = await validateInputs(PettyCashAccountsSchem, data)
        if (validationRes.error) {

            if(validationRes.error?.pettyCashUsersIds){
                dispatch(raiseToast({severity:'error', detail: validationRes.error?.pettyCashUsersIds, life: 3000}))
            }
            setFormErrors(validationRes.error)
        }
        else {
            setFormErrors({})
            setPettyCashAccountLoading(true)
            const insertResponse = await insertPettyCashAccount(data);
            if (insertResponse?.failure) {
                setPettyCashAccountLoading(false)
                const errorMessage = insertResponse?.data?.error;
                try {
                    const errorArray = JSON.parse(errorMessage);
                    const userNamesWithError = getUsersNamesFromIds(usersList, errorArray);
                    return dispatch(raiseToast({severity:'error', summary: "New Account", detail: `The following pettyCashUsers users already exist: ` + userNamesWithError.join(',\n'), life: 3000}))
                } catch (error) {
                    return dispatch(raiseToast({severity:'error', summary: "New Account", detail: errorMessage, life: 3000}))
                }
            }
            else{
                setPettyCashAccountLoading(false)
                setVisibleAddPettyCashAccount(false)
                let newPettyCashAccount = insertResponse.data?.newPettyCashAccount
                let newPettyCashUsers = insertResponse.data?.newPettyCashUsers
                newPettyCashAccount.accountUsers = newPettyCashUsers.map(item => ({
                    userId : item?.userId
                }))
                dispatch(updatePettyCashAccountsData([newPettyCashAccount, ...pettyCashAccountsData]))
                return dispatch(raiseToast({severity:'success', summary: "New Account", detail: 'Account has been created successfully.', life: 3000}))
            }
        }
    }

    const handleEditAccount = async () =>{
        let selectedUserIds = selectedUsers?.map(user => user.value)
        let updatedPrimaryUser = null
        if (initialValues?.userId != primaryUser){
            updatedPrimaryUser = primaryUser
        }
        let newPettyCashUserIds  = selectedUserIds.filter(id => !initialAccountUsers.includes(id));

        let newPettyCashUserDetails = newPettyCashUserIds?.map(userId => ({
            userId : userId,
            pettyCashAccountId : selectedPettyCashAccountsId,
            statusId : 1
        }));
        const data = {
            updatedPrimaryUser : updatedPrimaryUser,
            newPettyCashUsers: newPettyCashUserDetails,
            newPettyCashUserIds : newPettyCashUserIds,
            removedUsers: removedUsers,
            updatedBy: userId,
            updatedAt: new Date(),
        }
        if(selectedUsers.length == 0){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'Please select atleast one account user.', life: 3000}))
        }
        if(!primaryUser){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'Please select Primary Account Holder.', life: 3000}))
        }
        if(!(newPettyCashUserIds.length > 0 || updatedPrimaryUser || removedUsers.length > 0)){
            return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: 'No Changes found.', life: 3000}))
        }
        setPettyCashAccountLoading(true)
        const updatedResponse = await updatePettyCashAccount(selectedPettyCashAccountsId, data)
        if (updatedResponse?.failure) {
            setPettyCashAccountLoading(false)
            const errorMessage = updatedResponse?.data?.error;
            try {
                const errorArray = JSON.parse(errorMessage);
                const userNamesWithError = getUsersNamesFromIds(usersList, errorArray);
                return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: `The following pettyCashUsers already exist: ` + userNamesWithError.join(',\n'), life: 3000}))
            } catch (error) {
                return dispatch(raiseToast({severity:'error', summary: "Edit Account", detail: errorMessage, life: 3000}))
            }
        }
        else{
            setPettyCashAccountLoading(false)
            setVisibleAddPettyCashAccount(false)
            const data = updatedResponse?.data

            let updatedPettyCashAccountsData = pettyCashAccountsData.map(account => {
                if (account?.pettyCashAccountsId === selectedPettyCashAccountsId) {
                    let updatedAccount = { ...account };

                    if (data?.data?.updatedPrimaryUser !== null) {
                        updatedAccount.userId = data.data.updatedPrimaryUser;
                    }
                    let updatedAccountUsers = [...updatedAccount.accountUsers];
                
                    if (data?.newPettyCashUserIds?.length > 0) {
                        data.newPettyCashUserIds.forEach(id => {
                            updatedAccountUsers.push({ userId: id });
                        });
                    }
                    updatedAccount.accountUsers = updatedAccountUsers;

                    if (data?.removedUsers?.length > 0) {
                        updatedAccount.accountUsers = updatedAccount.accountUsers.filter(user => !data.removedUsers.includes(user.userId));
                    }
                    return updatedAccount;
                } else {
                    return account;
                }
            });
            dispatch(updatePettyCashAccountsData(updatedPettyCashAccountsData))
            return dispatch(raiseToast({severity:'success', summary: "Edit Account", detail: 'Account has been updated successfully.', life: 3000}))
        }
        
    }

    const options = usersList?.map(user => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.userId 
    }));

    return (
        <div className="grid mx-2">
            <div className="flex flex-column col-12 mb-2 md:col-6 lg:col-6 lg:mb-0">
                <label htmlFor="accountName" style={{fontSize:"12px", marginBottom:"3px"}}>
                    Account Name <span style={{ color: "red" }} className="from-required">*</span>
                </label>
                <TextInputField id="accountName" value={accountName} errorMessage={formErrors.pettyCashAccount} onChange={(e) => setAccountName(e.target.value)} disabled={title === 'EDIT'} required/>
            </div>
            <div className="flex flex-column col-12 mb-2 md:col-6 lg:col66 lg:mb-0">
                <label htmlFor="selectedUsers" style={{fontSize:"12px", marginBottom:"3px"}}>
                    Select Account User <span style={{ color: "red" }} className="from-required">*</span>
                </label>
                <Select 
                    id="selectedUsers" 
                    value={selectedUsers} 
                    options={options} 
                    isMulti 
                    onChange={(selectedOptions) => {setSelectedUsers(selectedOptions); }}
                    styles={{
                        menu: (provided, state) => ({
                            ...provided,
                            maxHeight: '200px',
                            overflowY: 'auto',
                        }),
                    }}
                    closeMenuOnSelect={false}
                    errorMessage={formErrors.selectedUserIds}
                />

            </div>
            <div className="flex flex-column col-12 mb-2 md:col-6 lg:col-6 lg:mb-0">
                <label htmlFor="primaryUser" style={{fontSize:"12px", marginBottom:"3px"}}>
                    Primary Account Holder <span style={{ color: "red" }} className="from-required">*</span>
                </label>
                <DropDownField id="primaryUser" value={primaryUser} options={selectedUsers} errorMessage={formErrors.userId} onChange={(e) => setPrimaryUser(e.target.value)} required/>
            </div>
            {/* <div className="flex flex-column col-12 mb-2 md:col-6 lg:col-6 lg:mb-0">
                <label htmlFor="accountType" style={{fontSize:"12px", marginBottom:"3px"}}>
                    Account Type
                </label>
                <DropDownField id="accountType" value={accountType} options={[{ label: 'Developer', value: 'developer' }]} onChange={(e) => setAccountType(e.target.value)} />
            </div>
            <div className="flex flex-column col-12 mb-2 md:col-6 lg:col-6 lg:mb-0">
                <label htmlFor="status" style={{fontSize:"12px", marginBottom:"3px"}}>
                    Status
                </label>
                <DropDownField id="status" value={status} options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]} onChange={(e) => setStatus(e.target.value)} />
            </div> */}
            <div className='flex flex-row mt-2 justify-end mt-5 ml-auto'>
                <div className="ml-auto">
                    <Button label={title === 'ADD' ? 'ADD ACCOUNT' : 'SAVE CHANGES'} onClick={title === 'ADD' ? handleAddAccount : handleEditAccount} />
                </div>
            </div>  
        </div>
    );
};

export default AddPettyCashAccountModal;
